import * as React from 'react';
import { compose, setDisplayName, withHandlers, withState, withProps, lifecycle } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { Fade } from 'ui/transitions';
import { makeSentenceFragments, FragmentRenderer, renderFragment } from 'util/makeSentenceFragments';
import { Input } from 'ui/shared/Input';

type DayProps = {
    enabled: boolean;
    n: number;
    textKey: string;
    days: boolean[];
    handleChangeDay: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const Day = ({ enabled, n, days, textKey, handleChangeDay, t }: DayProps & TranslateProps) =>
    <label>
        <input
            type="checkbox"
            id={ `edit-report-weekly-${n}` }
            value={ n }
            checked={ days[n] }
            onChange={ handleChangeDay }
            disabled={ !enabled }
        />
        { t(textKey) }
    </label>
;

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const RawRecurrenceWeekly = ({ sentenceFragments, renderFragment, ...props }: RecomposeProps & TranslateProps) =>
    <Fade
        id="edit-schedule-recurrence-panel-weekly"
        className="edit-schedule-recurrence-panel"
        visible={ props.enabled }
        duration={ 400 }
    >
        <div>
            <div>
                { sentenceFragments.map(renderFragment) }
            </div>
            <div className="flex">
                <Day n={ 1 } textKey="379" { ...props }/>
                <Day n={ 2 } textKey="380" { ...props }/>
                <Day n={ 3 } textKey="381" { ...props }/>
                <Day n={ 4 } textKey="382" { ...props }/>
            </div>
            <div className="flex">
                <Day n={ 5 } textKey="383" { ...props }/>
                <Day n={ 6 } textKey="384" { ...props }/>
                <Day n={ 0 } textKey="385" { ...props }/>
            </div>
        </div>
    </Fade>
;

type RecomposeProps = {
    enabled: boolean;
    sentenceFragments: string[];
    renderFragment: FragmentRenderer;
    days: boolean[];
    frequency: string;
    handleChangeFrequency: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeDay: React.ChangeEventHandler<HTMLInputElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setFrequency: (f: string) => void;
    _setDays: (d: boolean[]) => void;
};

export const RecurrenceWeekly = compose<OwnProps & RecomposeProps & TranslateProps, OwnProps>(
    setDisplayName('RecurrenceWeekly'),
    translate(),
    withProps(({ reportSettings }: OwnProps) => ({
        enabled: reportSettings.scheduleType === 'Weekly',
    })),
    withState('days', '_setDays', ({ reportSettings }: OwnProps) => reportSettings.scheduleDays),
    withState('frequency', '_setFrequency', ({ reportSettings }: OwnProps) =>
        reportSettings.scheduleType === 'Weekly'
            ? reportSettings.scheduleFrequency
            : 1,
    ),
    withHandlers({
        handleChangeFrequency: ({ onChange, _setFrequency }: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            _setFrequency(e.target.value);
            const frequency = parseInt(e.target.value, 10);
            onChange(reportSettings => reportSettings.setScheduleFrequency(frequency));
        },
        handleChangeDay: ({ onChange, days, _setDays }: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const day = e.target.value;
            days[day] = e.target.checked;
            _setDays([ ...days ]);
            onChange(reportSettings => reportSettings.setScheduleDays(days));
        },
    }),
    lifecycle({
        componentWillUpdate({ enabled, onChange, days, frequency }: OwnProps & _RecomposeProps) {
            // If this component becomes enabled, need to apply the current frequency value
            // to make sure the XML reflects the current values in the UI.
            if (!this.props.enabled && enabled) {
                onChange(reportSettings =>
                    reportSettings
                        .setScheduleDays(days)
                        .setScheduleFrequency(parseInt(frequency, 10))
                );
            }
        },
    }),
    withProps(({ frequency, t, enabled, ...props }: RecomposeProps & TranslateProps) => ({
        sentenceFragments: makeSentenceFragments(t('WC_REPORT_SCHEDULE_WEEKLY_SENTENCE', { count: parseInt(frequency, 10) })),
        renderFragment: renderFragment({
            INTERVAL: (
                <Input
                    type="number"
                    id="edit-report-weekly-weeks"
                    value={ frequency }
                    onChange={ props.handleChangeFrequency }
                    disabled={ !enabled }
                    min={ 1 }
                    max={ 52 }
                />
            ),
        }),
    })),
)(RawRecurrenceWeekly);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/schedule/runOnSchedule/recurrence/RecurrenceWeekly.tsx